import {Injectable} from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {catchError, map, Observable, shareReplay, tap, throwError} from "rxjs";
import {environment} from "../../../environments/environment";

/**
 * Lädt und parst die MOS-Vorhersage-Textdateien (z.B. "01_Aachen_Vorhersage.txt").
 *
 * Die Datei enthält Zeilen wie:  T_min_Wuerzburg="11";
 * Es wird eine Map von normalisiertem Schlüssel → Wert erstellt,
 * z.B. "t_min_wuerzburg" → "11".
 *
 * ⚠️ **Der Abruf geht über den eigenen Rest-Service** (`forecast/mos/{datei}`), nicht
 * direkt an die Wetterdaten-API. Der Direktabruf scheitert im Browser an CORS: die
 * Wetterdaten-API beantwortet den GET zwar mit `Access-Control-Allow-Origin`, einen
 * **OPTIONS-Preflight** aber mit **405 ohne CORS-Header** – der Browser bricht dann ab,
 * bevor der GET losgeht ("CORS-Anfrage schlug fehl", Statuscode null). Server-zu-Server
 * gibt es keinen Preflight. Nebenbei entfällt damit die Local-Network-Access-Sperre, die
 * den Direktabruf in der Cloud ohnehin verhindert (`enableMeasurementsFallback`).
 */
@Injectable({
  providedIn: 'root'
})
export class MeteoMeasurementsService {

  // Zeile: NAME="WERT";  – Kommentarzeilen (/*"..."*/;) werden ignoriert.
  // ⚠️ Der Name darf Umlaute tragen: die Dateien schreiben Orte wie in der Stationsliste
  // (`T_n_Nümbrecht_an_dem_Lindchen`, `P_vm_Lügde`). Ein Muster nur aus ASCII-Buchstaben ließ
  // diese Zeilen aus, die Boxen blieben leer (Bonn „Bergisches Land“, Bielefeld „Lügde“ – bis
  // 05.11.2026). Erst nach dem Treffer wird der Name normalisiert (normalizeKey), auf beiden
  // Seiten gleich, deshalb trifft der Box-Schlüssel mit Umlaut den Dateischlüssel mit Umlaut.
  // Spiegel: MosForecastParser.LINE_REGEX im Rest-Service.
  private readonly LINE_REGEX = /^\s*([\p{L}\p{N}_]+)\s*=\s*"(-?\d+(?:[.,]\d+)?)"/u;

  // Pro Datei einmal laden und cachen.
  private readonly cache = new Map<string, Observable<Map<string, string>>>();
  /**
   * `Last-Modified` je Datei, wie ihn der Rest-Service von der Wetterdaten-API durchreicht –
   * der "Stand" der Daten im Kasten "MSWR-MOS geladen". Null, wenn die API keinen schickt.
   */
  private readonly staende = new Map<string, Date | null>();

  constructor(private http: HttpClient) {
  }

  /**
   * Lädt die angegebene Vorhersagedatei und liefert eine Map normalisierter Schlüssel → Wert.
   * @param fileName z.B. "01_br_Vorhersage.txt"
   */
  getMeasurements(fileName: string): Observable<Map<string, string>> {
    const cached = this.cache.get(fileName);
    if (cached) {
      console.debug(`MOS: ${fileName} liegt schon vor, kein erneuter Abruf`);
      return cached;
    }

    // Ohne eigene Header und ohne Query-Parameter: so bleibt es ein einfacher GET, den
    // der Browser nicht vorab mit OPTIONS anfragt. Den API-Key setzt der Rest-Service.
    const url = `${environment.terminalApiUrl}/forecast/mos/${encodeURIComponent(fileName)}`;

    console.debug(`MOS: hole ${fileName} -> GET ${url}`);
    const request$ = this.http.get(url, {responseType: 'text', observe: 'response'}).pipe(
      tap(antwort => {
        const lastModified = antwort.headers.get('Last-Modified');
        const stand = lastModified ? new Date(lastModified) : null;
        this.staende.set(fileName, stand && !isNaN(stand.getTime()) ? stand : null);
        console.debug(`MOS: ${fileName} geliefert, ${antwort.body?.length ?? 0} Zeichen, Stand ${lastModified ?? 'unbekannt'}`);
      }),
      map(antwort => antwort.body ?? ''),
      map(text => {
        const werte = this.parse(text);
        console.debug(`MOS: ${fileName} geparst, ${werte.size} Schlüssel`
          + (werte.size === 0 ? ' – die Datei enthält keine Wertzeilen!' : ''));
        return werte;
      }),
      catchError(fehler => {
        // Alles ausgeben, was zur Ursache führt: Statuscode, Text und die Meldung des
        // Browsers. Bei einem CORS-Abbruch ist der Status 0 und nur die Meldung sagt etwas.
        console.error(`MOS: ${fileName} nicht ladbar.`, {
          url,
          status: fehler?.status,
          statusText: fehler?.statusText,
          meldung: fehler?.message,
          antwort: typeof fehler?.error === 'string' ? fehler.error : undefined
        });
        this.cache.delete(fileName);   // nicht den Fehlschlag merken, damit ein zweiter Klick es neu versucht
        return throwError(() => fehler);
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );
    this.cache.set(fileName, request$);
    return request$;
  }

  /**
   * Verwirft die gemerkten Dateien.
   *
   * Nötig für "MSWR-MOS laden": ohne das würde der Knopf nur die beim ersten Mal geholte
   * Fassung erneut eintragen, statt die aktuelle Datei von der Wetterdaten-API zu holen.
   */
  clearCache(): void {
    this.cache.clear();
    this.staende.clear();
  }

  /** Von wann die zuletzt geladene Datei ist (`Last-Modified`), oder null. */
  stand(fileName: string): Date | null {
    return this.staende.get(fileName) ?? null;
  }

  /** Parst den Textinhalt in eine Map normalisierter Schlüssel → Wert. */
  private parse(text: string): Map<string, string> {
    const result = new Map<string, string>();
    for (const line of text.split(/\r?\n/)) {
      const match = line.match(this.LINE_REGEX);
      if (match) {
        result.set(this.normalizeKey(match[1]), match[2]);
      }
    }
    return result;
  }

  /** Normalisiert Schlüssel: kleinschreiben und Umlaute ersetzen (Nürnberg → nuernberg). */
  normalizeKey(key: string): string {
    return key
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss');
  }
}
